import React from "react";
import { Container } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import "./Terms.css";
import "react-awesome-slider/dist/styles.css";

/*
 * Content model
 * -------------
 * Each block is one of:
 *   { t: "p",  text }            paragraph (use "\n" for a line break)
 *   { t: "ul", items: [...] }    bulleted list
 *   { t: "ol", items: [...] }    numbered list
 *
 * Inline formatting inside any text string:
 *   **bold**        -> <strong>
 *   email addresses -> mailto links
 */

const META = {
  effective: "5 October 2026",
  updated: "1 October 2026",
  version: "1.0",
};

const INTRO = [
  {
    t: "p",
    text:
      "Welcome to the website of **GBR Farming Private Limited** (“**GBR Farming**”, “**GBR**”, “**we**”, “**us**” or “**our**”).",
  },
  {
    t: "p",
    text:
      "These Terms & Conditions (“**Terms**”) govern your access to and use of the GBR Farming website, including its webpages, content, forms, digital interfaces, applications and other online resources made available through or in connection with the website (collectively, the “**Website**”).",
  },
  {
    t: "p",
    text:
      "By accessing, browsing, submitting information through, or otherwise using the Website, you acknowledge that you have read, understood and agree to be bound by these Terms and by the policies expressly incorporated into them.",
  },
  {
    t: "p",
    text: "If you do not agree with these Terms, please do not use the Website.",
  },
];

const SECTIONS = [
  {
    title: "1. About GBR Farming",
    blocks: [
      {
        t: "p",
        text:
          "GBR Farming Private Limited is an Indian company engaged in developing, providing and operating ecosystem infrastructure, technology-enabled ecosystem capabilities and related services.",
      },
      {
        t: "p",
        text:
          "GBR Farming may operate through different business divisions, including:",
      },
      {
        t: "ul",
        items: [
          "**GBR-Tech** — the Technology & Platform Division; and",
          "**GBR Farming Agrifood** — the Agriculture & Food Division.",
        ],
      },
      {
        t: "p",
        text:
          "These are divisions of GBR Farming Private Limited and do not, merely by their use as business or product names, constitute separate legal entities.",
      },
      {
        t: "p",
        text:
          "References on this Website to “GBR”, “GBR Farming”, “GBR-Tech” or “GBR Farming Agrifood” should be interpreted accordingly unless expressly stated otherwise.",
      },
    ],
  },
  {
    title: "2. Purpose and Scope of These Terms",
    blocks: [
      {
        t: "p",
        text:
          "These Terms apply to your use of the Website and its publicly available content and functionality.",
      },
      { t: "p", text: "The Website may provide information relating to:" },
      {
        t: "ul",
        items: [
          "GBR's ecosystem infrastructure;",
          "technology and platform capabilities;",
          "Ecosystem Network Operators (“**ENOs**”);",
          "Ecosystem Networks;",
          "Network Participants;",
          "ecosystem digital services and tools;",
          "agriculture and agrifood ecosystems;",
          "technology and infrastructure capabilities;",
          "business models and operating models;",
          "demonstrations and examples;",
          "enquiries, requests and applications;",
          "future or existing products and services; and",
          "other information or functionality made available by GBR from time to time.",
        ],
      },
      { t: "p", text: "These Terms govern general Website use." },
      {
        t: "p",
        text:
          "Specific GBR products, services, subscriptions, technology licences, implementation arrangements, ecosystem infrastructure arrangements or commercial relationships may be subject to separate agreements, order forms, statements of work, service agreements, subscription agreements, master agreements or other contractual documents.",
      },
      {
        t: "p",
        text:
          "Where a separate written agreement applies to a specific service or relationship, that agreement will govern the relevant service to the extent of any inconsistency with these general Website Terms.",
      },
    ],
  },
  {
    title: "3. Definitions",
    blocks: [
      { t: "p", text: "For purposes of these Terms:" },
      {
        t: "p",
        text:
          "**“Account”** means an account or authenticated access credential issued or enabled by GBR or an applicable ecosystem operator.",
      },
      {
        t: "p",
        text:
          "**“Content”** means text, information, documents, graphics, images, videos, software, designs, materials and other information made available through the Website.",
      },
      {
        t: "p",
        text:
          "**“Ecosystem Infrastructure”** means technology, infrastructure, systems, software, services, operational capabilities and related resources used to enable organisations to build, operate, govern and scale ecosystems.",
      },
      {
        t: "p",
        text:
          "**“Ecosystem Network”** means an ecosystem network established, operated or enabled through applicable GBR infrastructure or services.",
      },
      {
        t: "p",
        text:
          "**“Ecosystem Network Operator” or “ENO”** means an organisation that establishes and/or operates an Ecosystem Network using applicable GBR infrastructure, technology or services.",
      },
      {
        t: "p",
        text:
          "**“Network Participant”** means an organisation, individual, enterprise, service provider, producer, buyer, supplier, partner or other participant that participates in an Ecosystem Network.",
      },
      {
        t: "p",
        text:
          "**“Micro-Ecosystem”** means a smaller operational ecosystem or network established around a particular site, asset, user group, operational activity or other defined ecosystem context.",
      },
      {
        t: "p",
        text:
          "**“Services”** means services, technology, infrastructure, software, digital tools, professional services or other offerings provided by GBR.",
      },
      {
        t: "p",
        text:
          "**“Third-Party Services”** means products, services, websites, applications, platforms, APIs, infrastructure or content provided by parties other than GBR.",
      },
    ],
  },
  {
    title: "4. Acceptance of These Terms",
    blocks: [
      { t: "p", text: "By using the Website, you represent that:" },
      {
        t: "ol",
        items: [
          "you have the legal capacity to enter into these Terms;",
          "the information you provide to GBR is accurate and not misleading;",
          "where you act on behalf of an organisation, you are authorised to act on behalf of that organisation; and",
          "you will comply with these Terms and all applicable laws and regulations.",
        ],
      },
      {
        t: "p",
        text:
          "If you access the Website on behalf of an organisation, references to “you” include both you and, where applicable, that organisation.",
      },
      {
        t: "p",
        text:
          "Electronic acceptance, submission of information, or use of an online functionality may constitute acceptance of applicable terms where legally valid.",
      },
    ],
  },
  {
    title: "5. Website Use",
    blocks: [
      {
        t: "p",
        text:
          "You may use the Website for lawful purposes and in accordance with these Terms.",
      },
      { t: "p", text: "You must not use the Website:" },
      {
        t: "ul",
        items: [
          "for any unlawful purpose;",
          "to violate any applicable law or regulation;",
          "to impersonate another person or organisation;",
          "to provide false or misleading information;",
          "to interfere with the Website or its underlying systems;",
          "to attempt unauthorised access to systems, accounts or data;",
          "to introduce malware, malicious code or harmful material;",
          "to circumvent security or access controls;",
          "to infringe intellectual property rights;",
          "to violate privacy or other rights of another person;",
          "to conduct fraud or other deceptive activity;",
          "to disrupt the Website or its availability; or",
          "in any manner that may damage, disable, overburden or impair GBR's systems or the experience of other users.",
        ],
      },
      {
        t: "p",
        text:
          "GBR may restrict, suspend or terminate access where it reasonably believes that these Terms or applicable law have been violated.",
      },
    ],
  },
  {
    title: "6. Information Submitted Through the Website",
    blocks: [
      {
        t: "p",
        text:
          "The Website may allow you to submit information through forms, enquiries, applications, demo requests, partnership requests, feedback mechanisms or other interfaces.",
      },
      {
        t: "p",
        text:
          "You are responsible for ensuring that information submitted by you is:",
      },
      {
        t: "ul",
        items: [
          "accurate;",
          "complete to the extent reasonably required;",
          "lawful;",
          "not misleading;",
          "submitted with appropriate authority; and",
          "not infringing the rights of any third party.",
        ],
      },
      {
        t: "p",
        text:
          "You must not submit information that you do not have the right or authority to provide.",
      },
      {
        t: "p",
        text:
          "Submission of information does not by itself create a commercial relationship, partnership, agency, employment relationship, service agreement or other contractual relationship with GBR unless expressly agreed in writing.",
      },
    ],
  },
  {
    title: "7. Website Content and Information",
    blocks: [
      {
        t: "p",
        text:
          "The information published on the Website is provided primarily for general informational and business purposes.",
      },
      {
        t: "p",
        text:
          "GBR seeks to keep the Website useful and accurate; however, we do not warrant that all information will always be:",
      },
      {
        t: "ul",
        items: [
          "complete;",
          "current;",
          "accurate;",
          "error-free;",
          "uninterrupted; or",
          "suitable for every particular purpose.",
        ],
      },
      {
        t: "p",
        text:
          "Information may be changed, updated, corrected, suspended or removed without prior notice.",
      },
      {
        t: "p",
        text:
          "GBR may modify, discontinue or introduce Website features, services, content or functionality at any time.",
      },
    ],
  },
  {
    title: "8. No Professional or Specific Advice",
    blocks: [
      {
        t: "p",
        text:
          "Information published on the Website does not constitute legal, financial, investment, tax, agricultural, engineering, regulatory or other professional advice.",
      },
      {
        t: "p",
        text:
          "Descriptions of technology, ecosystem models, agricultural activities, operating models, business opportunities, commercial possibilities, environmental initiatives or other matters are provided for general information and discussion.",
      },
      {
        t: "p",
        text:
          "You should obtain appropriate professional advice before making decisions that require specialised expertise.",
      },
    ],
  },
  {
    title: "9. Business, Ecosystem and Performance Information",
    blocks: [
      {
        t: "p",
        text:
          "The Website may describe potential benefits, use cases, operating models, efficiencies, opportunities, ecosystem outcomes or examples.",
      },
      {
        t: "p",
        text:
          "Such information is illustrative and does not constitute a guarantee that a particular organisation will achieve a particular:",
      },
      {
        t: "ul",
        items: [
          "revenue;",
          "profit;",
          "return;",
          "productivity level;",
          "cost reduction;",
          "participant adoption;",
          "ecosystem scale;",
          "agricultural yield;",
          "operational result;",
          "environmental result; or",
          "other business or ecosystem outcome.",
        ],
      },
      {
        t: "p",
        text:
          "Actual outcomes may depend on numerous factors, including implementation, operating practices, market conditions, participant behaviour, data quality, technology availability, environmental conditions, regulatory requirements and other factors outside GBR's control.",
      },
      {
        t: "p",
        text:
          "Examples, projections, estimates, roadmaps and forward-looking statements are subject to change and should not be treated as guarantees.",
      },
    ],
  },
  {
    title: "10. Ecosystem Networks and Participant Relationships",
    blocks: [
      {
        t: "p",
        text:
          "GBR may provide infrastructure, technology and services that enable an organisation to build and operate an Ecosystem Network.",
      },
      {
        t: "p",
        text:
          "Unless expressly agreed otherwise in writing, GBR's provision of infrastructure or technology does not make GBR:",
      },
      {
        t: "ul",
        items: [
          "the seller or buyer in a transaction between participants;",
          "the employer of a participant;",
          "the agent of a participant;",
          "a partner or joint venture partner of a participant;",
          "a guarantor of a participant;",
          "a representative of a participant; or",
          "a party to every transaction or relationship between ecosystem participants.",
        ],
      },
      {
        t: "p",
        text:
          "Relationships between an ENO and its Network Participants, and relationships between Network Participants themselves, may be governed by separate agreements.",
      },
      {
        t: "p",
        text:
          "Each party remains responsible for its own contractual, commercial, operational, regulatory and legal obligations.",
      },
    ],
  },
  {
    title: "11. Responsibilities of ENOs",
    blocks: [
      {
        t: "p",
        text:
          "An ENO using GBR infrastructure or services may be responsible for matters including:",
      },
      {
        t: "ul",
        items: [
          "defining and managing its Ecosystem Network;",
          "onboarding and managing Network Participants;",
          "establishing appropriate participant terms;",
          "determining participant access;",
          "managing its commercial relationships;",
          "establishing applicable ecosystem policies;",
          "complying with applicable laws and regulations;",
          "managing its operational processes;",
          "protecting access credentials;",
          "ensuring appropriate use of ecosystem infrastructure; and",
          "managing matters that are specifically allocated to the ENO under its agreement with GBR.",
        ],
      },
      {
        t: "p",
        text:
          "The exact responsibilities of an ENO will depend on the applicable commercial and service agreements.",
      },
    ],
  },
  {
    title: "12. Responsibilities of Network Participants",
    blocks: [
      { t: "p", text: "Network Participants are responsible for:" },
      {
        t: "ul",
        items: [
          "providing accurate information;",
          "complying with applicable law;",
          "complying with applicable ENO rules and agreements;",
          "maintaining the security of their accounts and credentials;",
          "protecting their devices and systems;",
          "respecting the rights of other participants;",
          "fulfilling their contractual obligations; and",
          "obtaining all licences, permissions, registrations and approvals required for their activities.",
        ],
      },
      {
        t: "p",
        text:
          "GBR does not assume responsibility for a participant's independent business, regulatory compliance or contractual obligations unless expressly agreed otherwise.",
      },
    ],
  },
  {
    title: "13. Transactions Between Users or Participants",
    blocks: [
      {
        t: "p",
        text:
          "The Website or future GBR-enabled services may enable organisations or participants to discover, communicate with or transact with other parties.",
      },
      {
        t: "p",
        text:
          "Unless expressly stated otherwise in a specific written agreement, GBR is not a party to transactions entered into directly between users, ENOs or Network Participants.",
      },
      {
        t: "p",
        text:
          "Users and participants are responsible for independently evaluating:",
      },
      {
        t: "ul",
        items: [
          "counterparties;",
          "products and services;",
          "quality;",
          "quantity;",
          "pricing;",
          "payment terms;",
          "delivery;",
          "performance;",
          "warranties;",
          "taxes;",
          "regulatory requirements; and",
          "other transaction conditions.",
        ],
      },
      {
        t: "p",
        text:
          "Any transaction may be subject to separate terms agreed between the relevant parties.",
      },
    ],
  },
  {
    title: "14. Intellectual Property",
    blocks: [
      {
        t: "p",
        text:
          "Unless expressly stated otherwise, the Website and its Content are owned by, licensed to, or otherwise lawfully used by GBR.",
      },
      { t: "p", text: "This includes, as applicable:" },
      {
        t: "ul",
        items: [
          "trademarks;",
          "logos;",
          "brand names;",
          "designs;",
          "text;",
          "graphics;",
          "images;",
          "videos;",
          "software;",
          "source code;",
          "object code;",
          "platform components;",
          "architecture;",
          "documentation;",
          "methodologies;",
          "databases;",
          "interfaces;",
          "technical materials; and",
          "other proprietary materials.",
        ],
      },
      {
        t: "p",
        text:
          "Nothing in these Terms transfers ownership of GBR's intellectual property to you.",
      },
      {
        t: "p",
        text:
          "You may not reproduce, modify, distribute, publish, sell, license, commercially exploit, reverse engineer or create derivative works from GBR intellectual property except:",
      },
      {
        t: "ol",
        items: [
          "with GBR's prior written permission; or",
          "where such activity is expressly permitted by applicable law and cannot legally be restricted.",
        ],
      },
      {
        t: "p",
        text:
          "Use of GBR's trademarks or branding requires prior written permission unless otherwise permitted by law.",
      },
    ],
  },
  {
    title: "15. GBR Technology and Platform",
    blocks: [
      {
        t: "p",
        text:
          "Where GBR provides access to technology, software, applications, APIs, platforms, infrastructure or digital services, such access is provided under the applicable licence or commercial agreement.",
      },
      { t: "p", text: "Unless expressly permitted:" },
      {
        t: "ul",
        items: [
          "access does not transfer ownership;",
          "access is limited to the agreed purpose;",
          "credentials may not be shared with unauthorised persons;",
          "technology may not be copied or commercially exploited outside the agreed scope;",
          "users may not circumvent technical restrictions; and",
          "users may not attempt to gain unauthorised access to systems or data.",
        ],
      },
      { t: "p", text: "Additional technology-specific terms may apply." },
    ],
  },
  {
    title: "16. User Content and Data",
    blocks: [
      {
        t: "p",
        text:
          "Where you submit content or information to GBR, you represent that you have the necessary rights and authority to submit it.",
      },
      {
        t: "p",
        text:
          "You retain ownership of your content to the extent you own it, subject to any rights granted to GBR under an applicable agreement.",
      },
      {
        t: "p",
        text:
          "You grant GBR only those rights necessary to receive, process, store, communicate, secure and otherwise use the submitted information for the purposes for which it was submitted and as otherwise permitted by applicable law and contractual arrangements.",
      },
      {
        t: "p",
        text:
          "Personal data will be handled in accordance with GBR's **Privacy Policy** and applicable data-protection laws.",
      },
      {
        t: "p",
        text:
          "Nothing in these Terms overrides rights or obligations arising under applicable data-protection legislation.",
      },
    ],
  },
  {
    title: "17. Privacy",
    blocks: [
      {
        t: "p",
        text:
          "GBR may collect and process personal information in connection with Website use, enquiries, communications, applications and other activities.",
      },
      {
        t: "p",
        text:
          "The collection, use, storage, disclosure and protection of personal data are governed by GBR's Privacy Policy and applicable law.",
      },
      {
        t: "p",
        text:
          "The Privacy Policy forms part of the Website's legal framework but is maintained as a separate document.",
      },
      {
        t: "p",
        text:
          "Users should read the Privacy Policy before submitting personal information through the Website.",
      },
    ],
  },
  {
    title: "18. Artificial Intelligence, Analytics and Automated Outputs",
    blocks: [
      {
        t: "p",
        text:
          "GBR may develop or provide analytics, artificial intelligence, automation, decision-support or other technology-enabled capabilities.",
      },
      {
        t: "p",
        text:
          "Where such capabilities are provided, outputs may be generated using data, models, algorithms, rules or other computational methods.",
      },
      {
        t: "p",
        text:
          "Such outputs may contain errors, omissions or limitations and should be appropriately reviewed before being relied upon for material business, operational, financial, agricultural, regulatory or other decisions.",
      },
      {
        t: "p",
        text:
          "Unless expressly agreed otherwise, GBR does not warrant that an AI-generated, automated or analytical output will be complete, accurate, current or suitable for a particular purpose.",
      },
      {
        t: "p",
        text: "Users remain responsible for decisions made using such outputs.",
      },
    ],
  },
  {
    title: "19. Agricultural and Environmental Information",
    blocks: [
      {
        t: "p",
        text:
          "Where the Website contains agricultural, environmental, biological, climate, field, production or related information, such information is provided for general purposes.",
      },
      {
        t: "p",
        text:
          "Agricultural and environmental outcomes may vary significantly based on factors including:",
      },
      {
        t: "ul",
        items: [
          "location;",
          "weather;",
          "soil;",
          "biological conditions;",
          "crop and farming practices;",
          "water availability;",
          "pests and diseases;",
          "input quality;",
          "operational practices;",
          "market conditions; and",
          "other factors.",
        ],
      },
      {
        t: "p",
        text:
          "GBR does not guarantee any particular agricultural, biological, environmental or production outcome unless expressly agreed under a specific written agreement.",
      },
    ],
  },
  {
    title: "20. Third-Party Services and Links",
    blocks: [
      {
        t: "p",
        text:
          "The Website may contain links to or integrations with third-party websites, applications, services, technologies, APIs or resources.",
      },
      {
        t: "p",
        text:
          "Third-party services are controlled by their respective providers and may be subject to separate terms and privacy policies.",
      },
      {
        t: "p",
        text:
          "A link or integration does not necessarily constitute an endorsement, partnership or guarantee by GBR.",
      },
      {
        t: "p",
        text:
          "GBR is not responsible for third-party content, services, availability, security, accuracy, policies or practices except to the extent expressly required by applicable law or agreed in writing.",
      },
    ],
  },
  {
    title: "21. Availability and Security",
    blocks: [
      {
        t: "p",
        text:
          "GBR will take reasonable measures to maintain the Website and protect its systems; however, no online service can be guaranteed to be continuously available or completely secure.",
      },
      {
        t: "p",
        text: "The Website may be temporarily unavailable due to:",
      },
      {
        t: "ul",
        items: [
          "maintenance;",
          "upgrades;",
          "technical failures;",
          "telecommunications failures;",
          "internet outages;",
          "cloud or infrastructure failures;",
          "security incidents;",
          "third-party service interruptions; or",
          "circumstances beyond GBR's reasonable control.",
        ],
      },
      {
        t: "p",
        text:
          "You must not attempt to compromise the security, integrity or availability of the Website or any associated system.",
      },
    ],
  },
  {
    title: "22. Pricing and Commercial Information",
    blocks: [
      {
        t: "p",
        text:
          "Where prices, packages, service descriptions or commercial information are displayed on the Website, they are subject to change unless expressly stated otherwise.",
      },
      {
        t: "p",
        text:
          "Website pricing or service information does not constitute a binding commercial offer unless expressly stated.",
      },
      {
        t: "p",
        text:
          "Specific pricing, scope, service levels, payment obligations, implementation arrangements and other commercial conditions may be governed by separate written agreements.",
      },
      { t: "p", text: "Applicable taxes, duties or charges may apply." },
    ],
  },
  {
    title: "23. Confidential Information",
    blocks: [
      {
        t: "p",
        text:
          "Information published publicly on the Website is not confidential merely because it is commercially sensitive.",
      },
      {
        t: "p",
        text:
          "Confidential information exchanged during discussions between GBR and another organisation may be subject to a separate confidentiality agreement or other applicable contractual terms.",
      },
      {
        t: "p",
        text:
          "Users should not submit confidential or proprietary information through a public Website form unless the relevant GBR interface expressly indicates that such information may be submitted.",
      },
    ],
  },
  {
    title: "24. Acceptable Use and Prohibited Activities",
    blocks: [
      {
        t: "p",
        text:
          "You must not use the Website or any GBR-provided digital service to:",
      },
      {
        t: "ol",
        items: [
          "violate applicable law;",
          "infringe intellectual property rights;",
          "violate privacy rights;",
          "impersonate another person or organisation;",
          "submit fraudulent or misleading information;",
          "distribute malware or malicious code;",
          "gain unauthorised access;",
          "interfere with system security;",
          "disrupt service availability;",
          "scrape or extract information through unauthorised means;",
          "reverse engineer technology except where legally permitted;",
          "circumvent access controls;",
          "use another person's credentials without authorisation;",
          "upload unlawful or harmful content;",
          "facilitate fraud or other unlawful activities; or",
          "otherwise misuse GBR systems or services.",
        ],
      },
      {
        t: "p",
        text:
          "GBR may investigate suspected misuse and take appropriate action, including restricting or terminating access and referring matters to appropriate authorities where required or permitted by law.",
      },
    ],
  },
  {
    title: "25. Disclaimer",
    blocks: [
      {
        t: "p",
        text:
          "**To the maximum extent permitted by applicable law, the Website and its Content are provided on an “as available” and “as is” basis.**",
      },
      {
        t: "p",
        text:
          "GBR does not make or provide any representation or warranty that the Website or its Content will always be:",
      },
      {
        t: "ul",
        items: [
          "accurate;",
          "complete;",
          "current;",
          "uninterrupted;",
          "secure;",
          "error-free;",
          "free from viruses or other harmful components; or",
          "suitable for a particular purpose.",
        ],
      },
      { t: "p", text: "GBR does not warrant or guarantee:" },
      {
        t: "ul",
        items: [
          "any specific commercial result;",
          "revenue or profit;",
          "investment return;",
          "ecosystem growth;",
          "participant adoption;",
          "productivity improvement;",
          "agricultural yield;",
          "environmental outcome;",
          "operational performance;",
          "availability of third-party services; or",
          "achievement of any projected or described outcome,",
        ],
      },
      {
        t: "p",
        text: "unless expressly guaranteed under a separate written agreement.",
      },
      {
        t: "p",
        text:
          "Descriptions of future products, features, capabilities, business models, roadmaps, projections or opportunities are subject to change and should not be interpreted as guarantees.",
      },
      {
        t: "p",
        text:
          "Nothing in this Disclaimer excludes any representation, warranty, liability or statutory right that cannot lawfully be excluded or limited.",
      },
    ],
  },
  {
    title: "26. Limitation of Liability",
    blocks: [
      {
        t: "p",
        text:
          "To the maximum extent permitted by applicable law, GBR will not be liable for indirect, incidental, special, consequential or punitive losses, or for loss of profits, revenue, business opportunity, anticipated savings, goodwill or data, arising from or related to use of the Website or reliance on its Content.",
      },
      {
        t: "p",
        text:
          "GBR will not be responsible for losses resulting from matters outside its reasonable control, including failures of third-party networks, telecommunications providers, cloud infrastructure, external services, devices or systems not controlled by GBR.",
      },
      {
        t: "p",
        text:
          "Where GBR provides a specific paid service under a separate written agreement, liability relating to that service will be governed by the liability provisions of that agreement.",
      },
      {
        t: "p",
        text:
          "Nothing in these Terms limits liability to the extent such limitation is prohibited by applicable law.",
      },
    ],
  },
  {
    title: "27. Indemnification",
    blocks: [
      {
        t: "p",
        text:
          "To the extent permitted by applicable law, you agree to indemnify and hold harmless GBR Farming Private Limited, its officers, employees and authorised representatives from claims, losses, liabilities, damages, costs and expenses arising from:",
      },
      {
        t: "ul",
        items: [
          "your breach of these Terms;",
          "your unlawful use of the Website;",
          "your violation of another person's rights;",
          "your infringement of intellectual property rights;",
          "information or content submitted by you;",
          "your misuse of GBR technology or infrastructure; or",
          "your violation of applicable law.",
        ],
      },
      {
        t: "p",
        text:
          "The specific indemnification obligations applicable to an ENO, customer or other commercial counterparty may be governed by a separate agreement.",
      },
    ],
  },
  {
    title: "28. Suspension and Termination",
    blocks: [
      {
        t: "p",
        text:
          "GBR may suspend or restrict access to the Website or any GBR-provided service where reasonably necessary, including where:",
      },
      {
        t: "ul",
        items: [
          "these Terms are breached;",
          "unlawful activity is suspected;",
          "security is threatened;",
          "misuse is detected;",
          "required by law or governmental authority;",
          "a commercial agreement permits suspension; or",
          "continued access creates a material operational or security risk.",
        ],
      },
      {
        t: "p",
        text:
          "Termination or suspension does not affect rights or obligations that by their nature are intended to survive termination.",
      },
    ],
  },
  {
    title: "29. Regulatory Compliance",
    blocks: [
      {
        t: "p",
        text:
          "You are responsible for complying with all laws and regulations applicable to your activities.",
      },
      {
        t: "p",
        text:
          "Depending on the nature of your activities, these may include applicable requirements relating to:",
      },
      {
        t: "ul",
        items: [
          "information technology;",
          "data protection;",
          "consumer protection;",
          "intellectual property;",
          "taxation;",
          "agriculture;",
          "environment;",
          "employment;",
          "product safety;",
          "trade;",
          "payments;",
          "import/export; and",
          "sector-specific regulation.",
        ],
      },
      {
        t: "p",
        text:
          "GBR does not provide legal or regulatory advice through the Website.",
      },
    ],
  },
  {
    title: "30. Grievances and Complaints",
    blocks: [
      {
        t: "p",
        text:
          "Questions, complaints or legal notices relating to the Website may be submitted through the contact details provided below.",
      },
      {
        t: "p",
        text:
          "Where a specific statutory grievance mechanism applies to a GBR service, the applicable service-specific mechanism will apply.",
      },
      {
        t: "p",
        text:
          "**Grievance / Legal Contact:**\n**Avikesh**\n**Director**\n**Email:** admin@gbrapp.com",
      },
    ],
  },
  {
    title: "31. Changes to These Terms",
    blocks: [
      {
        t: "p",
        text: "GBR may modify these Terms from time to time to reflect:",
      },
      {
        t: "ul",
        items: [
          "changes to the Website;",
          "changes to services;",
          "changes in technology;",
          "legal or regulatory requirements;",
          "security requirements; or",
          "changes in business practices.",
        ],
      },
      {
        t: "p",
        text:
          "The updated Terms will be published on the Website with a revised “Last Updated” date.",
      },
      {
        t: "p",
        text:
          "Where required by applicable law, GBR will provide additional notice of material changes.",
      },
      {
        t: "p",
        text:
          "Your continued use of the Website after the effective date of revised Terms constitutes acceptance of the revised Terms to the extent legally permissible.",
      },
    ],
  },
  {
    title: "32. Governing Law",
    blocks: [
      {
        t: "p",
        text:
          "These Terms are governed by and interpreted in accordance with the laws of India, without regard to conflict-of-law principles.",
      },
      {
        t: "p",
        text:
          "Subject to the dispute-resolution provisions of any applicable written commercial agreement, courts having appropriate jurisdiction in **Bengaluru, Karnataka, India** shall have jurisdiction over disputes arising from or relating to these Terms.",
      },
    ],
  },
  {
    title: "33. Dispute Resolution",
    blocks: [
      {
        t: "p",
        text:
          "GBR and the relevant user will endeavour to resolve disputes relating to these Terms through good-faith discussions.",
      },
      {
        t: "p",
        text:
          "Where a separate written agreement between GBR and a customer or ENO contains dispute-resolution provisions, those provisions will apply to disputes arising under that agreement.",
      },
      {
        t: "p",
        text:
          "Nothing in this section prevents either party from seeking urgent or interim relief from a court of competent jurisdiction where legally available.",
      },
    ],
  },
  {
    title: "34. No Partnership or Agency",
    blocks: [
      {
        t: "p",
        text:
          "Use of the Website or participation in a GBR-enabled ecosystem does not by itself create a partnership, joint venture, agency, employment, franchise or fiduciary relationship between you and GBR.",
      },
      {
        t: "p",
        text:
          "No person may represent that they have authority to bind GBR unless expressly authorised in writing.",
      },
    ],
  },
  {
    title: "35. Severability",
    blocks: [
      {
        t: "p",
        text:
          "If any provision of these Terms is determined to be invalid, unlawful or unenforceable, that provision will be interpreted or modified to the minimum extent necessary to make it enforceable where legally permissible.",
      },
      {
        t: "p",
        text:
          "The remaining provisions will continue in full force and effect.",
      },
    ],
  },
  {
    title: "36. Waiver",
    blocks: [
      {
        t: "p",
        text:
          "Failure by GBR to enforce any provision of these Terms does not constitute a waiver of that provision or GBR's right to enforce it later.",
      },
    ],
  },
  {
    title: "37. Assignment",
    blocks: [
      {
        t: "p",
        text:
          "You may not assign or transfer your rights or obligations under these Terms without GBR's prior written consent, except where such restriction is not permitted by applicable law.",
      },
      {
        t: "p",
        text:
          "GBR may assign or transfer its rights or obligations in connection with a corporate reorganisation, merger, acquisition, restructuring or transfer of relevant business or assets, subject to applicable law.",
      },
    ],
  },
  {
    title: "38. Entire Agreement",
    blocks: [
      {
        t: "p",
        text:
          "These Terms, together with the Privacy Policy and any other policies or agreements expressly incorporated by reference, constitute the terms governing your general use of the Website.",
      },
      {
        t: "p",
        text:
          "For specific GBR services, the applicable commercial agreement, order form, statement of work, service agreement or licence agreement may contain additional or different terms.",
      },
      {
        t: "p",
        text:
          "In the event of conflict, the specific written agreement will govern the relevant service or transaction to the extent stated in that agreement.",
      },
    ],
  },
  {
    title: "39. Contact Information",
    blocks: [
      {
        t: "p",
        text:
          "For questions, legal notices, grievances or other communications regarding these Terms or the Website, please contact:",
      },
      { t: "p", text: "**GBR Farming Private Limited**" },
      {
        t: "p",
        text:
          "**Registered Office:**\n49054, SDA,\nPanthur Main Road, Balagere,\nBangalore - 560087, Karnataka, India",
      },
      { t: "p", text: "**CIN:**\nU74999KA2021PTC143080" },
      { t: "p", text: "**General Contact:**\nadmin@gbrapp.com" },
      { t: "p", text: "**Legal / Notices:**\nadmin@gbrapp.com" },
      { t: "p", text: "**Grievance Contact:**\nadmin@gbrapp.com" },
      { t: "p", text: "**Website:**\nhttps://gbrapp.com/" },
    ],
  },
  {
    title: "40. Effective Date",
    blocks: [
      {
        t: "p",
        text: `**Effective Date:** ${META.effective}\n**Last Updated:** ${META.updated}\n**Version:** ${META.version}`,
      },
    ],
  },
];

// Splits text on **bold**, emails and URLs and returns React nodes.
const INLINE_PATTERN = /(\*\*[^*]+\*\*|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}|https?:\/\/[^\s<]+)/g;

function renderInline(text, keyPrefix) {
  return text.split(INLINE_PATTERN).map((part, i) => {
    if (!part) return null;
    const key = `${keyPrefix}-${i}`;

    if (part.startsWith("**") && part.endsWith("**")) {
      // Bold segments may themselves contain emails/URLs; keep it simple and recurse.
      return <strong key={key}>{renderInline(part.slice(2, -2), key)}</strong>;
    }
    if (/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(part)) {
      return (
        <a key={key} href={`mailto:${part}`}>
          {part}
        </a>
      );
    }
    if (/^https?:\/\//.test(part)) {
      return (
        <a key={key} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      );
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

// Renders text that may contain "\n" line breaks.
function renderText(text, keyPrefix) {
  return text.split("\n").map((line, i) => (
    <React.Fragment key={`${keyPrefix}-l${i}`}>
      {i > 0 && <br />}
      {renderInline(line, `${keyPrefix}-l${i}`)}
    </React.Fragment>
  ));
}

function renderBlock(block, key) {
  switch (block.t) {
    case "ul":
      return (
        <ul key={key}>
          {block.items.map((item, i) => (
            <li key={`${key}-${i}`}>{renderInline(item, `${key}-${i}`)}</li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol key={key}>
          {block.items.map((item, i) => (
            <li key={`${key}-${i}`}>{renderInline(item, `${key}-${i}`)}</li>
          ))}
        </ol>
      );
    default:
      return <p key={key}>{renderText(block.text, key)}</p>;
  }
}

// Main component for Terms and Conditions page
export default class TermsComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  render() {
    return (
      <>
        <Container className="SignUpasContainerLightGray terms-container">
          <Grid container spacing={0} justifyContent="center">
            <Grid item md={10}>
              <h2 className="container-title">Terms &amp; Conditions</h2>

              <p className="terms-meta">
                <strong>Effective Date:</strong> {META.effective}
                <br />
                <strong>Last Updated:</strong> {META.updated}
                <br />
                <strong>Version:</strong> {META.version}
              </p>

              {INTRO.map((block, i) => renderBlock(block, `intro-${i}`))}

              {SECTIONS.map((section, s) => (
                <section className="terms-section" key={`sec-${s}`}>
                  <h3>{section.title}</h3>
                  {section.blocks.map((block, b) =>
                    renderBlock(block, `sec-${s}-${b}`)
                  )}
                </section>
              ))}

             {/*  <p className="terms-copyright">
                <strong>© 2026 GBR Farming Private Limited. All rights reserved.</strong>
              </p> */}
            </Grid>
          </Grid>
        </Container>
      </>
    );
  }
}
